import isServer from '@framework/router/Next/isServer';
import { CacheEntry, buildCacheKey, getCached, setCache, cachePromiseWithEviction } from './helpers';

export interface IServerCacheOptions {
  isAnonymous?: boolean,
}

interface WithServerCacheOptions {
  ttlSeconds?: number,
  scope?: string,
  keyGenerator?: (scope: string, method: string, args: unknown[]) => string,
  anonymousOnly?: boolean,
  maxEntries?: number,
}

const GLOBAL_SCOPE = 'global';
const DEFAULT_CACHE_SECONDS = 300;
const MAX_ENTRIES_PER_METHOD = 10;

const caches: Map<string, CacheEntry>[] = [];

/** @internal Exposed for testing */
export { caches as __caches };

/**
 * Server-side in-memory cache with TTL.
 * Stores the Promise on first call — concurrent requests get the same Promise (deduplication).
 * Client-side calls pass through without caching.
 */
export function withServerCache(options: WithServerCacheOptions = {}) {
  const {
    ttlSeconds = DEFAULT_CACHE_SECONDS,
    scope = GLOBAL_SCOPE,
    keyGenerator,
    anonymousOnly,
    maxEntries = MAX_ENTRIES_PER_METHOD,
  } = options;

  return (_target: object, key: string, descriptor: PropertyDescriptor): PropertyDescriptor => {
    if (typeof descriptor?.value !== 'function') {
      return descriptor;
    }

    const originalFn = descriptor.value;
    // One Map per decorated method: its ceiling cannot evict any other method's entries.
    const cache = new Map<string, CacheEntry>();

    caches.push(cache);

    descriptor.value = function decorator (...args: unknown[]) {
      if (!isServer()) {
        return originalFn.apply(this, args);
      }

      // Anonymous-only methods take `{ isAnonymous }` as their last argument.
      const keyArgs = anonymousOnly ? args.slice(0, -1) : args;

      if (anonymousOnly && !(args[args.length - 1] as IServerCacheOptions)?.isAnonymous) {
        return originalFn.apply(this, args);
      }

      const cacheKey = keyGenerator
        ? keyGenerator(scope, key, keyArgs)
        : buildCacheKey(scope, key, keyArgs);
      const cached = getCached(cache, cacheKey);

      if (cached !== undefined) {
        return cached;
      }

      const result = originalFn.apply(this, args);

      if (result instanceof Promise) {
        const promise = cachePromiseWithEviction(result, cache, cacheKey);

        setCache(cache, cacheKey, promise, ttlSeconds * 1000, maxEntries);

        return promise;
      }

      setCache(cache, cacheKey, result, ttlSeconds * 1000, maxEntries);

      return result;
    };

    return descriptor;
  };
}
